// @ts-nocheck
/**
 * <category-drilldown> — /search 카테고리 1차>2차>3차 단일 선택 드릴인(인라인) 필터.
 *
 * 동작:
 *  - 처음엔 1차 목록만 노출.
 *  - 1차 선택 → 나머지 1차 숨김, 선택한 1차만 헤더로 남고 그 아래 2차 목록 펼침,
 *    선택한 1차 행 오른쪽에 X(해제) 버튼 노출.
 *  - 2차 선택 → 나머지 2차 숨김, 3차 체크박스 펼침, 2차 행에 X 노출.
 *  - X 클릭 → 해당 레벨 선택 해제(상위 목록 복귀). 그 가지에 체크된 3차가 있으면 해제 후 필터 갱신.
 *  - 활성 3차는 항상 단일 2차 (다른 2차의 체크는 직렬화 전 capture 단계에서 해제).
 *
 * 마크업/스타일은 snippets/cascading-category-filter.liquid 에서 nested 로 서버 렌더된다.
 * 선택 상태는 행의 aria-expanded(true=선택) 로 표현하며, X 버튼은 그때만 CSS 로 노출된다.
 *
 * 외부 모듈로 분리한 이유: /search 결과 섹션이 morph 로 주입돼도 전역 1회 로드된
 * module 스크립트가 자동 업그레이드하기 때문.
 */
class CategoryDrilldown extends HTMLElement {
  connectedCallback() {
    if (this.selectedCat1 == null) this.selectedCat1 = '';
    if (this.selectedCat2 == null) this.selectedCat2 = '';
    // 사용자가 1차/2차 행을 직접 선택했는지(=실제 facet 으로 조회할지) 여부.
    // true 일 때만 category_depth_1/2 hidden input 을 폼에 넣어 results 카운트에 반영한다.
    if (this.cat1Explicit == null) this.cat1Explicit = false;
    if (this.cat2Explicit == null) this.cat2Explicit = false;

    this.onClick = this.onClick.bind(this);
    this.onChangeCapture = this.onChangeCapture.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
    this.addEventListener('click', this.onClick);
    // facet-inputs-component(on:change, 버블)보다 먼저 실행되도록 capture 단계
    this.addEventListener('change', this.onChangeCapture, true);

    // 드로어를 '결과 보기' 없이 닫으면(취소) 편집분을 URL(마지막 적용 상태)로 되돌린다.
    // dialog:close 는 조상 dialog-component 에서 발생하므로 직접 바인딩(버블 안 함).
    this.dialogComponent = this.closest('dialog-component');
    if (this.dialogComponent) this.dialogComponent.addEventListener('dialog:close', this.onDialogClose);

    this.syncFromDOM();

    // morph 재렌더(필터 적용)로 자식이 교체되면 선택 상태를 다시 보정
    this.observer = new MutationObserver(() => {
      if (this._internalMutation) return;
      this.syncFromDOM();
    });
    this.observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChangeCapture, true);
    if (this.dialogComponent) this.dialogComponent.removeEventListener('dialog:close', this.onDialogClose);
    if (this.observer) this.observer.disconnect();
  }

  // 드로어 닫힘(취소/적용 공통). 취소면 편집분을 URL 기준으로 복원한다.
  // 적용(결과 보기)은 morph 로 이 요소가 이미 적용 상태로 재렌더된 뒤라 URL 로 재구성해도 동일 → 무해.
  onDialogClose() {
    this.resetToUrl();
  }

  // URL(마지막 적용 상태) 기준으로 3차 체크박스·1/2차 드릴인·hidden facet input·펼침 DOM 을 완전 복원한다.
  // dialog.js 의 폼 스냅샷 복원(#restoreFormState)은 체크박스만 되돌려 drilldown 의
  // JS 상태(selectedCat1/2, cat1/2Explicit)·동적 hidden input 을 되돌리지 못하므로 여기서 보완한다.
  resetToUrl() {
    const params = new URLSearchParams(window.location.search);
    const urlCat1 = params.get('filter.p.m.custom.category_depth_1') || '';
    const urlCat2 = params.get('filter.p.m.custom.category_depth_2') || '';
    const urlCat3 = params.getAll('filter.p.m.custom.category_depth_3');

    // 3차 체크박스를 URL 활성값으로 복원
    this._internalMutation = true;
    this.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
      cb.checked = urlCat3.indexOf(cb.value) !== -1;
    });
    this._internalMutation = false;

    // 1/2차 선택(facet) 상태를 URL 기준으로 복원
    this.selectedCat1 = urlCat1;
    this.selectedCat2 = urlCat2;
    this.cat1Explicit = !!urlCat1;
    this.cat2Explicit = !!urlCat2;

    // URL 에 명시 1차가 없고 활성 3차만 있으면, 그 3차의 부모 경로로 펼침만 복원(facet 아님)
    if (!this.selectedCat1 && urlCat3.length) {
      const firstChecked = this.querySelector('input[type="checkbox"]:checked');
      const li = firstChecked && firstChecked.closest('[data-cat1]');
      if (li) {
        this.selectedCat1 = li.dataset.cat1 || '';
        if (!this.selectedCat2) this.selectedCat2 = li.dataset.cat2 || '';
      }
    }

    // render() 가 hidden facet input 을 cat1/2Explicit·selectedCat1/2 기준으로 재동기화(불필요분 제거)
    this.render();
  }

  onClick(event) {
    // X(해제) 버튼 — 행 클릭보다 먼저 처리
    const deselect = event.target.closest('[data-deselect]');
    if (deselect && this.contains(deselect)) {
      event.preventDefault();
      if (deselect.dataset.deselect === 'cat1') {
        this.selectedCat1 = '';
        this.selectedCat2 = '';
        this.cat1Explicit = false;
        this.cat2Explicit = false;
      } else {
        this.selectedCat2 = '';
        this.cat2Explicit = false;
      }
      this.clearSelections();
      this.render();
      // 해제로 present 트리가 넓어질 수 있으므로(예: cat2 제거로 다른 1차 복귀),
      // 적용된(URL) 상태 기준 + 카테고리 필터 제외로 트리를 재구성한다.
      this.refreshTreeFromApplied();
      // 1차/2차 facet 해제분을 results 카운트에 반영
      this.triggerFilterUpdate();
      return;
    }

    const cat2Row = event.target.closest('button.cdf__row--cat2');
    if (cat2Row && this.contains(cat2Row)) {
      event.preventDefault();
      this.selectedCat2 = cat2Row.dataset.cat2 || '';
      this.cat2Explicit = true;
      this.render();
      // 선택한 2차 행 카운트로 Résultats 를 즉시 프리뷰(API 왕복 대기 없음).
      this.updateResultCountPreview();
      this.triggerFilterUpdate();
      return;
    }

    const cat1Row = event.target.closest('button.cdf__row--cat1');
    if (cat1Row && this.contains(cat1Row)) {
      event.preventDefault();
      this.selectedCat1 = cat1Row.dataset.cat1 || '';
      this.selectedCat2 = '';
      this.cat1Explicit = true;
      this.cat2Explicit = false;
      this.render();
      // 선택한 1차 행 카운트로 Résultats 를 즉시 프리뷰(API 왕복 대기 없음).
      this.updateResultCountPreview();
      this.triggerFilterUpdate();
      return;
    }
    // 체크박스 클릭은 기본 동작 유지 (facet-inputs-component 가 갱신)
  }

  // 체크된 3차가 있으면 그 경로로 선택 상태를 맞춘다(자동 드릴인). 없으면 기존 선택을 유지하되
  // 현재 DOM 에 더 이상 없는 선택은 리셋한다.
  syncFromDOM() {
    const checked = Array.from(this.querySelectorAll('input[type="checkbox"]:checked'));
    if (checked.length) {
      const firstLi = checked[0].closest('[data-cat1]');
      this.selectedCat1 = (firstLi && firstLi.dataset.cat1) || '';
      this.selectedCat2 = (firstLi && firstLi.dataset.cat2) || '';
      this._internalMutation = true;
      checked.forEach((cb) => {
        const li = cb.closest('[data-cat1]');
        if (li && (li.dataset.cat1 !== this.selectedCat1 || li.dataset.cat2 !== this.selectedCat2)) {
          cb.checked = false;
        }
      });
      this._internalMutation = false;
    } else {
      // morph(결과 보기 적용/전체해제/칩 제거) 직후엔 URL이 적용된 진실이다.
      // URL 의 category_depth_1/2 활성값으로 1차/2차 선택 상태를 복원/정리한다.
      const params = new URLSearchParams(window.location.search);
      const urlCat1 = params.get('filter.p.m.custom.category_depth_1') || '';
      const urlCat2 = params.get('filter.p.m.custom.category_depth_2') || '';

      if (urlCat1) {
        this.selectedCat1 = urlCat1;
        this.cat1Explicit = true;
      } else if (this.cat1Explicit) {
        this.selectedCat1 = '';
        this.selectedCat2 = '';
        this.cat1Explicit = false;
        this.cat2Explicit = false;
      }
      if (urlCat2) {
        this.selectedCat2 = urlCat2;
        this.cat2Explicit = true;
      } else if (this.cat2Explicit) {
        this.selectedCat2 = '';
        this.cat2Explicit = false;
      }

      if (this.selectedCat1 && !this.querySelector('.cdf__group--cat1[data-cat1="' + cssEscape(this.selectedCat1) + '"]')) {
        this.selectedCat1 = '';
        this.selectedCat2 = '';
        this.cat1Explicit = false;
        this.cat2Explicit = false;
      }
      if (
        this.selectedCat2 &&
        !this.querySelector(
          '.cdf__group--cat2[data-cat1="' + cssEscape(this.selectedCat1) + '"][data-cat2="' + cssEscape(this.selectedCat2) + '"]'
        )
      ) {
        this.selectedCat2 = '';
        this.cat2Explicit = false;
      }
    }
    this.render();
  }

  render() {
    const c1Selected = this.selectedCat1;
    const c2Selected = this.selectedCat2;

    this.querySelectorAll('.cdf__group--cat1').forEach((g) => {
      const isSel = !!c1Selected && g.dataset.cat1 === c1Selected;
      g.hidden = !!c1Selected && !isSel;
      const row = g.querySelector('.cdf__row--cat1');
      if (row) row.setAttribute('aria-expanded', isSel ? 'true' : 'false');
      const children = g.querySelector('.cdf__children');
      if (children) children.hidden = !isSel;
    });

    this.querySelectorAll('.cdf__group--cat2').forEach((g) => {
      const isSel = !!c2Selected && g.dataset.cat2 === c2Selected && g.dataset.cat1 === c1Selected;
      g.hidden = !!c2Selected && !isSel;
      const row = g.querySelector('.cdf__row--cat2');
      if (row) row.setAttribute('aria-expanded', isSel ? 'true' : 'false');
      const ul = g.querySelector('.cdf__checkboxes');
      if (ul) ul.hidden = !isSel;
    });

    // 선택 상태를 폼의 category_depth_1/2 hidden input 에 반영 (results 카운트용)
    this.syncCategoryFacetInputs();
  }

  // 명시적으로 선택된 1차/2차를 폼의 hidden facet input 으로 동기화한다.
  // 폼(form)은 category-drilldown 의 조상이라 MutationObserver(자식 감시) 대상이 아니다.
  syncCategoryFacetInputs() {
    this.setFacetInput('filter.p.m.custom.category_depth_1', this.cat1Explicit ? this.selectedCat1 : '');
    this.setFacetInput('filter.p.m.custom.category_depth_2', this.cat2Explicit ? this.selectedCat2 : '');
  }

  // 폼에 hidden facet input 을 추가/갱신/제거. value 가 비면 제거한다.
  setFacetInput(name, value) {
    const form = this.closest('form');
    if (!form) return;
    let input = form.querySelector('input[data-cdf-facet="' + name + '"]');
    if (value) {
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.setAttribute('data-cdf-facet', name);
        form.appendChild(input);
      }
      input.value = value;
    } else if (input) {
      input.remove();
    }
  }

  // facet-inputs-component 를 통해 results 카운트 프리뷰를 갱신한다.
  triggerFilterUpdate() {
    const inputs = this.closest('facet-inputs-component');
    if (inputs && typeof inputs.updateFilters === 'function') inputs.updateFilters();
  }

  // 트리 후보군 + 카운트는 "적용된(URL) 상태 + 카테고리 필터 제외" 기준으로 유지한다.
  // 확정 전(pending) 드로어 편집(다른 필터 체크, cat 선택)에 따라 좁아지면 안 되고,
  // 적용된 카테고리 필터로도 collapse 되면 안 되므로, window.location 에서 category_depth_*
  // 파람을 제거한 URL 로 섹션을 fetch 해 이 인스턴스의 트리(.cdf__list)만 교체한다.
  async refreshTreeFromApplied() {
    const url = new URL(window.location.href);
    ['filter.p.m.custom.category_depth_1', 'filter.p.m.custom.category_depth_2', 'filter.p.m.custom.category_depth_3'].forEach(
      (p) => url.searchParams.delete(p)
    );
    // /search: q 비어있으면 * (search-page-rules)
    if (/\/search\/?$/.test(url.pathname) && !url.searchParams.get('q')) url.searchParams.set('q', '*');

    let html;
    try {
      html = await (await fetch(url.toString())).text();
    } catch (e) {
      return;
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // 같은 인스턴스(floating-panel id)의 트리를 매칭. 못 찾으면 문서 내 첫 드릴다운으로 폴백.
    const panel = this.closest('floating-panel-component');
    let newList = null;
    if (panel && panel.id) {
      const newPanel = doc.getElementById(panel.id);
      if (newPanel) newList = newPanel.querySelector('.cdf__list');
    }
    if (!newList) newList = doc.querySelector('category-drilldown .cdf__list');

    const currentList = this.querySelector('.cdf__list');
    if (!newList || !currentList) return;

    // innerHTML 교체는 childList 변이를 일으켜 observer→syncFromDOM 을 부른다.
    // deferred 라 URL 에 아직 해제 전 cat1/2 가 남아 syncFromDOM 이 트리를 다시 collapse
    // 시킬 수 있으므로 교체 동안 observer 를 끊는다.
    if (this.observer) this.observer.disconnect();
    currentList.innerHTML = newList.innerHTML;
    if (this.observer) this.observer.observe(this, { childList: true, subtree: true });

    // 새 트리에 현재 선택 상태(selectedCat1/2)를 반영(펼침/숨김 재적용)
    this.render();
  }

  // 현재 필터의 선택된 3차 전부 해제 (facet 갱신은 호출측에서 triggerFilterUpdate 로 처리)
  clearSelections() {
    const checked = Array.from(this.querySelectorAll('input[type="checkbox"]:checked'));
    if (!checked.length) return;
    this._internalMutation = true;
    checked.forEach((cb) => { cb.checked = false; });
    this._internalMutation = false;
  }

  // 새로 체크된 3차와 다른 2차에 속한 기존 체크를 직렬화 전에 해제 (단일 2차 강제)
  onChangeCapture(event) {
    const input = event.target;
    if (!input || input.type !== 'checkbox') return;
    if (input.checked) {
      const li = input.closest('[data-cat1]');
      if (li) {
        const cat1 = li.dataset.cat1 || '';
        const cat2 = li.dataset.cat2 || '';
        this._internalMutation = true;
        this.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
          if (cb === input) return;
          const l = cb.closest('[data-cat1]');
          if (l && (l.dataset.cat1 !== cat1 || l.dataset.cat2 !== cat2)) {
            cb.checked = false;
          }
        });
        this._internalMutation = false;
      }
    }
    // 체크/해제 즉시 Résultats 카운트를 프리뷰한다(API 왕복 대기 없음).
    this.updateResultCountPreview();
  }

  // 3차는 상호배타적(한 상품=한 3차)이라 체크된 3차 카운트의 합 = 결과 수.
  // 체크된 3차가 없으면 선택된 2차(또는 1차) 행의 카운트로 복귀한다.
  // 백그라운드 previewFilters 가 이후 권위있는 값으로 재확정하므로 여긴 즉시 프리뷰용.
  updateResultCountPreview() {
    const checked = this.querySelectorAll('.cdf__checkbox-item input[type="checkbox"]:checked');
    let count = null;
    if (checked.length > 0) {
      count = 0;
      checked.forEach((cb) => {
        const text = cb.closest('.facets__inputs-list-item')?.querySelector('.checkbox__count')?.textContent?.trim();
        const n = parseInt(text || '', 10);
        if (!Number.isNaN(n)) count += n;
      });
    } else {
      const base =
        this.querySelector('.cdf__row--cat2[aria-expanded="true"] .cdf__count')?.textContent?.trim() ??
        this.querySelector('.cdf__row--cat1[aria-expanded="true"] .cdf__count')?.textContent?.trim();
      if (base != null) {
        const n = parseInt(base, 10);
        if (!Number.isNaN(n)) count = n;
      }
    }
    if (count == null) return;

    document.querySelectorAll('.facets__see-results span').forEach((el) => {
      el.textContent = `(${count})`;
    });
  }
}

function cssEscape(value) {
  if (window.CSS && typeof CSS.escape === 'function') return CSS.escape(value);
  return String(value).replace(/["\\]/g, '\\$&');
}

if (!customElements.get('category-drilldown')) {
  customElements.define('category-drilldown', CategoryDrilldown);
}
